// frontend/src/hooks/useEditableGrid.js
//
// Bulk-save scaffolding for AG Grid pages with inline-editable cells.
//
//   useEditableGridState({ getRowId })
//       — Just the dirty-tracking primitives. For pages whose save flow is
//         their own (ItemSetup's chained items + item-locations with 409
//         confirms, SupplierSetup's currency-change confirmation,
//         SupplierCatalog's terms batch then per-row default elections,
//         DataHealth's per-issueKey actions, ForecastGrid's
//         `{updates, creates}` split).
//
//   useEditableGridSave(opts)
//       — State + save + discard, for a grid with one mutation and one
//         payload shape. A response with `summary.failed > 0` shows the
//         failed rows and their reasons (utils/failedRows.js) and keeps only
//         THEIR edits queued: every bulk endpoint commits per row, and a
//         retry must not send a saved row again. On its own it is for a grid
//         that is not a page — a dialog over one.
//
//   useEditableGrid(opts)
//       — The page's hook: useEditableGridSave plus the two things only a
//         page owns, Ctrl+S and the leave-page guard. It returns an
//         `unsavedGuard` dialog element the page mounts once. The router
//         asks only the guard registered last, so a second one on screen
//         would silence the page's.

import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useKeyboardSave from './useKeyboardSave';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';
import { translateRequestError } from '../queries/api';
import { showToast } from '../components/common/appToastBus';
import { useDirtyTabReporter } from '../tabs/useDirtyTabs';
import { failedRowsMessage } from '../utils/failedRows';


/**
 * Dirty-tracking primitives for an editable AG Grid.
 *
 * @param {object}   opts
 * @param {(row) => string|number} opts.getRowId
 *   How to extract a stable identity from a row object. Used to key
 *   the changedCells map and to reconcile partial-success deletions.
 *
 * @returns {{
 *   changedCells: Map,            // render coupling
 *   changedRows: Array,           // for save payload construction
 *   isDirty: boolean,
 *   onCellValueChanged: (event) => void,  // wire to <AgGridReact onCellValueChanged>
 *   unsavedCellClass: (params) => boolean, // the dirty-cell test
 *   gridContext: object,          // wire to <AgGridReact context> — tints edited cells
 *   clearAll: () => void,
 *   clearByIds: (idSet) => void,  // partial-success path
 *   getChangedCellsRef: () => Map, // escape hatch for code that reads ref directly
 * }}
 */
export function useEditableGridState({ getRowId }) {
  const changedCellsRef = useRef(new Map());
  const [changedCells, setChangedCells] = useState(new Map());
  const [changedRows, setChangedRows] = useState([]);

  // Capture getRowId via a ref so the returned callbacks keep one identity
  // across renders even when the consumer passes an inline arrow function:
  // cellClassRules depend on unsavedCellClass, so a new identity would rebuild
  // the column defs. Updated in an effect, per the react-hooks/refs lint rule;
  // the callbacks always read .current.
  const getRowIdRef = useRef(getRowId);
  useEffect(() => {
    getRowIdRef.current = getRowId;
  }, [getRowId]);

  const isDirty = changedRows.length > 0;

  // Mirror the page's dirty flag into the tabs registry so the strip's
  // close button can prompt before discarding edits. No-op when rendered
  // outside the tabbed shell (TabIdContext is null).
  useDirtyTabReporter(isDirty);

  // The ag-cell-unsaved rule calls this for every visible cell on every
  // refresh, so the ref-based lookup is intentional (the state mirror exists
  // to drive re-renders, not to be read at cell-class-evaluation time).
  const unsavedCellClass = useCallback((params) => {
    if (!params.data) return false;
    const cellKey = `${getRowIdRef.current(params.data)}_${params.colDef.field}`;
    return changedCellsRef.current.has(cellKey);
  }, []);

  // Pure dirty-state recording — callable from pages with bespoke
  // onCellValueChanged logic (LocationSetup runs a Shopify-deactivate
  // confirm + auto-clears a paired field; ItemSetup buckets cells by
  // scope; etc.). Default `onCellValueChanged` is the convenience
  // wrapper around this + AG Grid's refreshCells call.
  const recordCellChange = useCallback(({ row, fieldName, oldValue, newValue }) => {
    if (!row || !fieldName) return;
    const rowId = getRowIdRef.current(row);
    const cellKey = `${rowId}_${fieldName}`;
    changedCellsRef.current.set(cellKey, {
      rowId, fieldName, oldValue, newValue, rowData: row,
    });
    setChangedCells(new Map(changedCellsRef.current));
    setChangedRows((prev) => {
      const idx = prev.findIndex((r) => getRowIdRef.current(r) === rowId);
      if (idx !== -1) {
        const updated = [...prev];
        updated[idx] = row;
        return updated;
      }
      return [...prev, row];
    });
  }, []);

  const onCellValueChanged = useCallback((event) => {
    recordCellChange({
      row: event.data,
      fieldName: event.colDef?.field,
      oldValue: event.oldValue,
      newValue: event.newValue,
    });
    // Force the visual update of the just-edited cell. Without this,
    // AG Grid's internal change-detection misses the cellClassRules
    // re-evaluation in some scenarios (rich-select editors particularly).
    if (event.api && event.node && event.colDef?.field) {
      event.api.refreshCells({
        force: true,
        rowNodes: [event.node],
        columns: [event.colDef.field],
      });
    }
  }, [recordCellChange]);

  const clearAll = useCallback(() => {
    setChangedRows([]);
    changedCellsRef.current.clear();
    setChangedCells(new Map());
  }, []);

  // Partial-success: drop only the rows whose ids appear in `idSet`.
  // ItemSetup uses this to keep IL-scope edits when the items batch
  // succeeded but the IL batch failed.
  const clearByIds = useCallback((idSet) => {
    const stringIds = new Set([...idSet].map(String));
    setChangedRows((prev) => prev.filter((r) => !stringIds.has(String(getRowIdRef.current(r)))));
    for (const [key, info] of changedCellsRef.current.entries()) {
      if (stringIds.has(String(info.rowId))) {
        changedCellsRef.current.delete(key);
      }
    }
    setChangedCells(new Map(changedCellsRef.current));
  }, []);

  // Cell-level partial success: drop cells matching `predicate(info)`.
  // Used when the failed batch and the dirty state don't decompose
  // cleanly along row boundaries — e.g. ItemSetup, where a single row
  // can carry both item-scoped and IL-scoped pending cells, and the
  // partial-failure alert promises to keep IL-scoped edits typed even
  // when the item-scope batch succeeded.
  const clearCellsByPredicate = useCallback((predicate) => {
    let touched = false;
    for (const [key, info] of changedCellsRef.current.entries()) {
      if (predicate(info)) {
        changedCellsRef.current.delete(key);
        touched = true;
      }
    }
    if (!touched) return;
    setChangedCells(new Map(changedCellsRef.current));
    // Rebuild changedRows: keep only rows that still have at least one
    // cell entry. Otherwise the dirty counter / Save button stay visible
    // for rows whose every dirty cell was just dropped.
    const rowsWithCells = new Set();
    for (const info of changedCellsRef.current.values()) {
      rowsWithCells.add(String(info.rowId));
    }
    setChangedRows((prev) => prev.filter((r) => rowsWithCells.has(String(getRowIdRef.current(r)))));
  }, []);

  const getChangedCellsRef = useCallback(() => changedCellsRef.current, []);

  // What the grid needs to tint an edited cell: pass as <AgGridReact context>.
  // defaultColDef's ag-cell-unsaved rule (utils/gridDefaults.js) reads it off
  // the context for every cell, so no column has to wire the tint itself.
  const gridContext = useMemo(() => ({ unsavedCellClass }), [unsavedCellClass]);

  return useMemo(() => ({
    changedCells, changedRows, isDirty,
    onCellValueChanged, recordCellChange, unsavedCellClass, gridContext,
    clearAll, clearByIds, clearCellsByPredicate, getChangedCellsRef,
  }), [
    changedCells, changedRows, isDirty,
    onCellValueChanged, recordCellChange, unsavedCellClass, gridContext,
    clearAll, clearByIds, clearCellsByPredicate, getChangedCellsRef,
  ]);
}


/**
 * Editable-grid save flow: the state primitives plus save and discard.
 *
 * @param {object}                       opts
 * @param {(opts?: {purge?: boolean}) => void} opts.refresh
 *   `refresh` from the page's useSsrmGrid. A discard re-reads the rows
 *   through it, in place (`purge: false`): the edited rows are the ones on
 *   screen. A save needs no re-read here — the mutation names what it
 *   changed (`queries/dataChanged`) and the grid reads its rows again once
 *   the edits are cleared — where they stand, when the mutation names the
 *   grid in its `edits`.
 * @param {(row) => string|number}       opts.getRowId
 * @param {object}                       opts.mutation
 *   useApiMutation handle. Must expose `.mutateAsync` and `.isPending`.
 * @param {(rows, cellsRef) => any}      opts.buildPayload
 *   Convert the dirty rows into the mutation's vars. The cellsRef gives
 *   access to the per-cell oldValue/newValue/fieldName.
 *
 * @param {string}                       [opts.successKey]
 *   i18n key with `{count}` interpolation, shown on full success.
 * @param {string}                       [opts.failureKey='msg.saveFailed']
 *   Fallback i18n key passed to translateRequestError on thrown errors.
 *
 * @returns {object}
 *   All primitives from useEditableGridState plus:
 *     isSaving:      boolean
 *     save:          () => Promise<void>
 *     discard:       () => void — drops the edits, re-reads the rows and
 *                    says so in a toast. A filter change that drops
 *                    them calls clearAll instead: its own reload re-reads.
 */
export function useEditableGridSave({
  refresh,
  getRowId,
  mutation,
  buildPayload,
  successKey,
  failureKey = 'msg.saveFailed',
}) {
  // Checked at mount rather than in `discard`: a page wired wrong should fail
  // the moment it renders, before anyone can type into it.
  if (typeof refresh !== 'function') {
    throw new Error(
      'useEditableGridSave: `refresh` is required — pass the page\'s useSsrmGrid refresh',
    );
  }
  const { t } = useTranslation();
  const state = useEditableGridState({ getRowId });

  const save = useCallback(async () => {
    if (!state.isDirty) return;
    try {
      const vars = buildPayload(state.changedRows, state.getChangedCellsRef());
      const data = await mutation.mutateAsync(vars);
      // Every bulk-update endpoint answers `{results, summary: {total,
      // succeeded, failed}}`.
      const { failed, succeeded } = data.summary;
      if (failed > 0) {
        state.clearByIds(data.results.filter((row) => row.success).map((row) => row.id));
        showToast(failedRowsMessage(t, data.results), 'error');
        return;
      }
      if (successKey) showToast(t(successKey, { count: succeeded }), 'success');
      state.clearAll();
    } catch (err) {
      console.error('useEditableGrid save:', err.message);
      showToast(translateRequestError(err, t, failureKey), 'error');
    }
  }, [state, buildPayload, mutation, t, successKey, failureKey]);

  const discard = useCallback(() => {
    state.clearAll();
    refresh({ purge: false });
    showToast(t('common.changesDiscarded'), 'info');
  }, [state, refresh, t]);

  return {
    ...state,
    isSaving: mutation.isPending,
    save,
    discard,
  };
}


/**
 * A PAGE's editable grid: `useEditableGridSave` (same options) plus Ctrl+S
 * and the unsaved-changes guard on leaving the page. Ctrl+S is silent while a
 * save runs, as the Save button is, so the same batch cannot be sent twice.
 *
 * @returns {object}
 *   Everything useEditableGridSave returns plus:
 *     unsavedGuard:  JSX | null   (mount this in the tree)
 */
export function useEditableGrid(opts) {
  const grid = useEditableGridSave(opts);

  useKeyboardSave(grid.save, grid.isDirty && !grid.isSaving);
  const unsavedGuard = useUnsavedChangesGuard(grid.isDirty);

  return { ...grid, unsavedGuard };
}
